import { useEffect, useMemo, useState } from 'react';

import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useTranslation } from 'next-i18next';
import { usePermissions } from '@lib/hooks';
import { routes, getSidebarSections } from '../../../config';
import MainSidebar from '../MainSidebar';
import MainNavbar from '../MainNavbar';
import PermissionDenied from '../PermissionDenied';

const MainLayoutRoot = styled('div')(({ theme }) => ({
  display: 'grid',
  width: '100%',
  height: '100vh',
  gridTemplateColumns: '1fr',
  gridTemplateRows: '64px 1fr',
  gridColumnGap: '0px',
  gridRowGap: '0px',
  transition: '300ms',
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: '280px 1fr',
  },
}));

const ChildrenWrapper = styled('div')(({ theme }) => ({
  gridArea: '2 / 1 / 3 / 2',
  display: 'flex',
  flex: '1 1 auto',
  maxWidth: '100%',
  flexDirection: 'column',
  overflowY: 'auto',
  [theme.breakpoints.up('lg')]: {
    gridArea: '2 / 2 / 3 / 3',
  },
}));

export default function MainLayout(props) {
  const { children, requiredAcl, fallback } = props;

  const router = useRouter();
  const { status } = useSession();
  const { t } = useTranslation();
  const permissions = usePermissions();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const sections = useMemo(() => getSidebarSections(t, router), [t, router]);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (status !== 'authenticated') {
      router.replace(`${routes.auth.login}?return_path=${router.asPath}`);
    }
  }, [router, router.isReady, status]);

  if (status !== 'authenticated') {
    return null;
  }

  const missingAcl = (requiredAcl || []).some((acl) => !permissions.includes(acl));
  const content = missingAcl ? (fallback || <PermissionDenied />) : children;

  return (
    <>
      <MainLayoutRoot>
        <ChildrenWrapper>
          {content}
        </ChildrenWrapper>
      </MainLayoutRoot>
      <MainNavbar onOpenSidebar={() => setIsSidebarOpen(true)} />
      <MainSidebar
        onClose={() => setIsSidebarOpen(false)}
        open={isSidebarOpen}
        sections={sections}
      />
    </>
  );
}

MainLayout.propTypes = {
  children: PropTypes.node,
  requiredAcl: PropTypes.arrayOf(PropTypes.string),
  fallback: PropTypes.node,
};

MainLayout.defaultProps = {
  children: null,
  requiredAcl: [],
  fallback: null,
};
